import { useState, useCallback } from 'react';
import { maintenancePlanService } from '../services/maintenancePlanService';
import { MaintenancePlan, MaintenanceTask, MaintenanceAlert } from '../types/maintenance';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export const useMaintenancePlan = (vehicleId: string) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const checkAdminRole = useCallback(async (organizationId: string): Promise<boolean> => {
    if (!user?.id) return false;
    try {
      const memberRef = doc(db, `organizations/${organizationId}/members/${user.id}`);
      const memberDoc = await getDoc(memberRef);
      return memberDoc.exists() && memberDoc.data()?.role === 'Admin';
    } catch (err) {
      console.error('Erreur lors de la vérification du rôle admin:', err);
      return false;
    }
  }, [user?.id]);

  const getPlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const plans = await maintenancePlanService.getVehiclePlans(vehicleId);
      return plans;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Une erreur est survenue';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  const generatePlan = useCallback(async (
    organizationId: string,
    make: string,
    model: string,
    year: number,
    userId: string
  ) => {
    setLoading(true);
    setError(null);
    try {
      const isAdmin = await checkAdminRole(organizationId);
      if (!isAdmin) {
        throw new Error('Vous devez être administrateur pour générer un plan de maintenance');
      }

      const plan = await maintenancePlanService.generatePlanFromPerplexity(
        vehicleId,
        organizationId,
        make,
        model,
        year,
        userId
      );
      return plan;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Une erreur est survenue';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [vehicleId, checkAdminRole]);

  const createCustomPlan = useCallback(async (
    plan: Omit<MaintenancePlan, 'id' | 'createdAt' | 'updatedAt'>
  ) => {
    setLoading(true);
    setError(null);
    try {
      const isAdmin = await checkAdminRole(plan.organizationId);
      if (!isAdmin) {
        throw new Error('Vous devez être administrateur pour créer un plan de maintenance');
      }

      const newPlan = await maintenancePlanService.createCustomPlan(plan);
      return newPlan;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Une erreur est survenue';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [checkAdminRole]);

  const updatePlan = useCallback(async (
    planId: string,
    updates: Partial<MaintenancePlan>
  ) => {
    setLoading(true);
    setError(null);
    try {
      if (updates.organizationId) {
        const isAdmin = await checkAdminRole(updates.organizationId);
        if (!isAdmin) {
          throw new Error('Vous devez être administrateur pour modifier un plan de maintenance');
        }
      }

      await maintenancePlanService.updatePlan(planId, updates);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Une erreur est survenue';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [checkAdminRole]);

  const deletePlan = useCallback(async (planId: string, organizationId: string) => {
    setLoading(true);
    setError(null);
    try {
      const isAdmin = await checkAdminRole(organizationId);
      if (!isAdmin) {
        throw new Error('Vous devez être administrateur pour supprimer un plan de maintenance');
      }

      await maintenancePlanService.deletePlan(planId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Une erreur est survenue';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [checkAdminRole]);

  const createAlert = useCallback(async (
    taskId: string,
    organizationId: string,
    dueDate: Date,
    priority: MaintenanceAlert['priority'],
    userId: string
  ) => {
    setLoading(true);
    setError(null);
    try {
      const isAdmin = await checkAdminRole(organizationId);
      if (!isAdmin) {
        throw new Error('Vous devez être administrateur pour créer une alerte');
      }

      const alert = await maintenancePlanService.createAlert(
        taskId,
        vehicleId,
        organizationId,
        dueDate,
        priority,
        userId
      );
      return alert;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Une erreur est survenue';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [vehicleId, checkAdminRole]);

  const getActiveAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const alerts = await maintenancePlanService.getActiveAlerts(vehicleId);
      return alerts;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Une erreur est survenue';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  return {
    loading,
    error,
    getPlans,
    generatePlan,
    createCustomPlan,
    updatePlan,
    deletePlan,
    createAlert,
    getActiveAlerts,
    checkAdminRole
  };
};
